import React from 'react'
import styled, { css } from 'styled-components'
import { Table, TableRow, TableHeadCell, TableCell as RawCell } from './Table'
import { CompanyMember } from '../models/client/company'
import { NumberDisplay } from './NumberDisplay'
import { Spacer } from './Spacer'
import { useDesktopMediaQuery } from '../styles/mixins/mediaQueryHooks'
import { Tag } from './Tag'
import { SPACING } from '../styles/mixins/constants'
import { text } from '../styles/mixins/text'
import { Icon } from './Icon'
import { Button } from './Button'
import { color } from '../styles'
import { useUser } from '../hooks/useUser'
import { useAuthorized } from '../hooks/useAuthorization'
import { Tooltip } from './Tooltip'
import { MESSAGE_UNAUTHORIZED, MESSAGE_USER_EDIT_UNAUTHORIZED } from '../content/tooltipMessages'
import { useMessage } from '../hooks/useMessage'

type Props = {
    className?: string
    members: CompanyMember[]
    operationsLoading?: boolean
    onEdit: (memberId: string) => void
}

const USER_INFO = ['Name', 'Email', 'Phone']

const LAPTOP_COL_NAMES = [...USER_INFO, 'Permissions', 'OPERATIONS']

const MOBILE_COL_NAMES = ['User', 'Permissions']

const LAPTOP_COL_SIZE = `repeat(4, minmax(200px, 1fr)) 125px`
const MOBILE_COL_SIZE = 'minmax(200px, 1fr) 1fr 50px'

export const PermissionsTable = (props: Props): JSX.Element => {
    const { className, members, operationsLoading, onEdit } = props
    const user = useUser()
    const canManageUsers = useAuthorized('users')
    const isDesktop = useDesktopMediaQuery()
    const messenger = useMessage()
    const labels = isDesktop ? LAPTOP_COL_NAMES : MOBILE_COL_NAMES
    const colSizes = isDesktop ? LAPTOP_COL_SIZE : MOBILE_COL_SIZE

    const renderMobile = (): JSX.Element[] =>
        members.map((member) => {
            const isCurrentUser = member?.id === user?.id
            const canManageUser = isCurrentUser || canManageUsers

            if (!canManageUser) {
                return
            }

            return (
                <StyledTableRow
                    columnSize={MOBILE_COL_SIZE}
                    isSelectable={canManageUser}
                    onClick={
                        canManageUser
                            ? () => onEdit(member.id)
                            : () => {
                                  messenger.danger(MESSAGE_USER_EDIT_UNAUTHORIZED)
                              }
                    }
                    key={member.id}
                >
                    <TableCell>
                        <UserCell>
                            <UserName>{member.name}</UserName>
                            <Spacer size={1} vertical />
                            <ContactColumn>
                                <ContactInfo>
                                    <Icon iconKey="Mail" size={1} />
                                    <Spacer size={2} />
                                    {member.email}
                                </ContactInfo>

                                {member.phone ? (
                                    <>
                                        <Spacer size={1} vertical />
                                        <ContactInfo>
                                            <Icon iconKey="Phone" size={1} />
                                            <Spacer size={2} />
                                            <NumberDisplay type="phone">
                                                {member.phone}
                                            </NumberDisplay>
                                        </ContactInfo>
                                    </>
                                ) : (
                                    <div />
                                )}
                            </ContactColumn>
                        </UserCell>
                    </TableCell>

                    <TableCell>
                        {member.permissions.map((permission) => (
                            <StyledTag key={permission} label={permission} tagKey={permission} />
                        ))}
                    </TableCell>
                    <TableCell>
                        <Tooltip
                            disabled={!canManageUser}
                            tipText={!canManageUser && MESSAGE_UNAUTHORIZED}
                        >
                            <Icon
                                size={5}
                                iconKey="Edit"
                                color={canManageUser ? 'primary' : 'muted'}
                                style={{
                                    display: !canManageUser && 'none',
                                }}
                            />
                        </Tooltip>
                        <Spacer size={4} />
                    </TableCell>
                </StyledTableRow>
            )
        })

    const renderDesktop = (): JSX.Element[] =>
        members.map((member) => {
            const isCurrentUser = member?.id === user?.id
            const canManageUser = isCurrentUser || canManageUsers

            if (!canManageUser) {
                return
            }

            return (
                <StyledTableRow key={member.id} columnSize={LAPTOP_COL_SIZE}>
                    <TableCell>{member.name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                        {member.phone ? (
                            <NumberDisplay type="phone">{member.phone}</NumberDisplay>
                        ) : (
                            '-'
                        )}
                    </TableCell>
                    <TableCell>
                        {member.permissions.map((permission) => (
                            <StyledTag key={permission} label={permission} tagKey={permission} />
                        ))}
                    </TableCell>
                    <TableCell center>
                        <Tooltip disabled={canManageUser} tipText={MESSAGE_USER_EDIT_UNAUTHORIZED}>
                            <Button
                                iconKey="Edit"
                                type="primary"
                                onClick={() => onEdit(member.id)}
                                label="Edit"
                                size="medium"
                                disabled={!!operationsLoading || !canManageUser}
                                style={{
                                    display: !canManageUser && 'none',
                                }}
                            />
                        </Tooltip>
                    </TableCell>
                </StyledTableRow>
            )
        })

    return (
        <StyledTable className={className}>
            <TableRow columnSize={colSizes}>
                {labels.map((colTitle) => (
                    <StyledTableHeadCell key={colTitle}>
                        {colTitle === 'OPERATIONS' ? '' : colTitle}
                    </StyledTableHeadCell>
                ))}
            </TableRow>
            <ScrollBox>{isDesktop ? renderDesktop() : renderMobile()}</ScrollBox>
        </StyledTable>
    )
}

const Divider = styled.div`
    width: 100%;
    background-color: ${color.border.primary};
    height: 1px;
`

const ScrollBox = styled.div`
    display: flex;
    flex-flow: column nowrap;
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
`

const StyledTable = styled(Table)`
    min-height: 0px;
    flex: 0 0 auto;
    max-height: 100%;
`

type ExtraRowType = {
    isSelectable?: boolean
}

const StyledTag = styled(Tag)`
    && {
        margin-right: ${SPACING[1]};
        padding: 0px ${SPACING[1]};
        &:last-child {
            margin-right: 0px;
        }
    }
`

const UserCell = styled.div`
    display: flex;
    flex-flow: column nowrap;
`

const UserName = styled.span<{ isStrong?: boolean }>`
    ${text('paragraph', 'primary', 'text', 'bold')}
`

const ContactColumn = styled.div`
    ${text('sub', 'secondary')}
    display: flex;
    flex-flow: column nowrap;
`

const ContactInfo = styled(ContactColumn)`
    flex-flow: row nowrap;
    align-items: center;
`

const StyledTableHeadCell = styled(TableHeadCell)`
    display: flex;
    flex-flow: row;
    border: 0px;
    ${(p: { centered?: boolean }) =>
        p.centered
            ? css`
                  justify-content: center;
              `
            : ''}
`

const TableCell = styled(RawCell)`
    display: flex;
    flex-flow: row nowrap;
    white-space: nowrap;
    border: 0px;
    ${(p: { center?: boolean }) =>
        p.center
            ? css`
                  justify-content: center;
              `
            : ''}
`

const StyledTableRow = styled(TableRow)`
    ${(p: ExtraRowType) =>
        p.isSelectable
            ? css`
                  cursor: pointer;
              `
            : ''}

    &:last-child {
        border-bottom: 0px;
    }
`
